/* eslint-disable react/prop-types */
/* eslint-disable consistent-return */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Container,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import slugify from 'slugify';
import {
  useListProductImagesQuery,
  useUploadProductImageMutation,
  useDeleteProductImageMutation,
} from '../services/productImages.endpoints';
import ConfirmDialog from './ConfirmDialog';
import {
  useCreateProductMutation,
  useEditProductMutation,
  useGetProductDetailsQuery,
} from '../services/products.endpoints';
import {
  CreateProductRequest,
  ProductFormSchema,
  SupportedHostApps,
} from '../schemas/products.schemas';
import {
  platformOptions,
  productCategoryOptions,
  productIdOptions,
} from '../constants/productDropdowns';
import productThumbnails from '../constants/productThumbnails';
import AssignSupportedHostApps from './AssignSupportedHostApps';
import MarkdownEditor from './MarkdownEditor';
import StableSlugSelector from './StableSlugSelector';

const getErrorMessage = (err: unknown): string => {
  if (!err || typeof err !== 'object') return '';
  const e = err as any;
  if (e?.data?.message) return e.data.message;
  if (e?.error) return e.error;
  if (e?.status) return `Request failed with status ${e.status}`;
  return 'An unknown error occurred';
};

const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;

const LazyImg = ({ src, alt, sx }) => {
  const ref = React.useRef(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <CardMedia
      component="img"
      ref={ref}
      src={loaded ? src : undefined}
      alt={alt}
      sx={{
        backgroundColor: '#f0f0f0', // placeholder gray
        ...sx,
      }}
    />
  );
};

interface ProductForm {
  name: string;
  category: string;
  priceId?: string;
  upgradeId?: string;
  downgradeId?: string;
  productId: CreateProductRequest['productId'] | '';
  addOnId?: string;
  // subscriptionPlans: SubscriptionPlan[];
  fullPrice?: number | string;
  hidden?: boolean;
  priority?: boolean;
  showBadge?: boolean;
  showInFooter?: boolean;
  keywords: string;
  sale?: {
    salePrice: number | '';
    until: string | null;
  } | null;
  briefDescription: string;
  productPage: string;
  downloadPage: string;
  supportedPlatforms: string[];
  supportedHostApplications: {
    displayName: string;
    id: string;
    version: string;
    priceOverride?: number | '';
    downloadPage?: string;
  }[];
  fullDescription: string;
  stableSlug?: string;
  isLatest?: boolean;
  // image?: File | string | null;
  productImageId?: string | null;
  useCustomUpgradeVersions?: boolean;
  validUpgradeFromVersions?: number[];
}

const initialProduct: ProductForm = {
  name: '',
  category: '',
  productId: '',
  addOnId: '',
  priceId: '',
  upgradeId: '',
  downgradeId: '',
  // subscriptionPlans: [],
  fullPrice: '',
  hidden: false,
  priority: false,
  showBadge: false,
  showInFooter: false,
  keywords: '',
  briefDescription: '',
  productPage: '',
  downloadPage: '',
  supportedPlatforms: ['Macintosh', 'Windows', 'All Platforms'],
  supportedHostApplications: [],
  fullDescription: '',
  stableSlug: '',
  isLatest: false,
  productImageId: null,
  useCustomUpgradeVersions: false,
  validUpgradeFromVersions: [],
};

interface ProductFormProps {
  mode: 'edit' | 'create';
}

const ProductFormPage = ({ mode }: ProductFormProps) => {
  const { id } = useParams<{ id: string }>();
  const [
    createProduct,
    {
      isLoading: createIsLoading,
      error: createError,
      isSuccess: createSuccess,
    },
  ] = useCreateProductMutation();
  const [
    editProduct,
    { isLoading: editIsLoading, error: editError, isSuccess: editSuccess },
  ] = useEditProductMutation();
  const {
    data: productDetails,
    isFetching: detailsAreLoading,
    error: detailsError,
  } = useGetProductDetailsQuery(id ?? '');
  const [productForm, setProductForm] = useState(initialProduct);
  const [formError, setFormError] = useState(false);

  const { data: images = [], isFetching: imagesAreLoading } =
    useListProductImagesQuery();
  const [uploadImage] = useUploadProductImageMutation();
  const [deleteImage] = useDeleteProductImageMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const askDelete = (idToDelete: string, name: string) => {
    setPendingDelete({ id: idToDelete, name });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await deleteImage(pendingDelete.id);
    setConfirmOpen(false);
    setPendingDelete(null);
  };

  const handleUpload = async (file: File) => {
    const name = prompt('Image display name?') || file.name;
    const key =
      prompt('Image key (slug)?') ||
      file.name
        .replace(/\..+$/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
    await uploadImage({ file, name, key });
  };

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadKey, setUploadKey] = useState('');

  // Load product details into the edit form
  useEffect(() => {
    if (mode === 'edit' && productDetails) {
      const updated = { ...productDetails };

      if (productDetails.image?.data && productDetails.image?.contentType) {
        updated.image = `data:${productDetails.image.contentType};base64,${productDetails.image.data}`;
      }

      setProductForm(updated);
    }
  }, [mode, productDetails]);

  useEffect(() => {
    if (mode === 'create' && createSuccess) {
      setProductForm(initialProduct);
    }
  }, [createSuccess, mode]);

  const isLoading =
    mode === 'create' ? createIsLoading : editIsLoading || detailsAreLoading;
  const error = mode === 'create' ? createError : editError || detailsError;
  const isSuccess = mode === 'create' ? createSuccess : editSuccess;

  console.log({ mode, error, formError });
  const {
    name,
    category,
    keywords,
    hidden,
    priority,
    showBadge,
    showInFooter,
    productId,
    priceId,
    upgradeId,
    downgradeId,
    fullPrice,
    sale,
    briefDescription,
    productPage,
    downloadPage,
    supportedPlatforms,
    supportedHostApplications,
    fullDescription,
    // image,
    productImageId,
  } = productForm;
  console.log('supportedHostApplications', supportedHostApplications);
  const { salePrice, until } = sale || {};

  const submitButtonTextOptions = {
    edit: 'Save Edits',
    create: 'Create Product',
  };
  const submitButtonText = submitButtonTextOptions[mode];

  // const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setProductForm({ ...productForm, [event.target.name]: event.target.value });
  // };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: key, value } = event.target;
    console.log('handleChange', { key, value });

    // if (key === 'name') {
    //   const auto = slugify(value, { lower: true, strict: true })
    //     .replace(/\d+(\.\d+)?$/g, '') // remove version numbers
    //     .replace(/\b(upgrade|crossgrade)\b.*$/i, '');

    //   setProductForm((prev) => ({
    //     ...prev,
    //     name: value,
    //     // stableSlug: prev.stableSlug || auto,
    //   }));
    //   return;
    // }

    // // If switching dropdown image while a custom upload exists → clear the file
    // if (key === 'productId' && productForm.image instanceof File) {
    //   setProductForm({
    //     ...productForm,
    //     [key]: value,
    //     image: null, // reset to null so backend clears it
    //   });
    //   return;
    // }

    setProductForm({ ...productForm, [key]: value });
  };

  // const handleSubmit = (e: React.SyntheticEvent) => {
  //   e.preventDefault();
  //   try {
  //     const validated = ProductFormSchema.parse(productForm);
  //     if (validated.fullPrice === '') {
  //       delete validated.fullPrice;
  //     }

  //     // If user unchecked the box we *explicitly* ask the API to wipe sale
  //     if (productForm.sale === undefined) {
  //       // choose whichever the API contract prefers
  //       validated.sale = null; // simplest
  //       // or: validated.sale = { salePrice: null, until: null };
  //     }

  //     if (mode === 'create') {
  //       createProduct(validated);
  //     } else {
  //       editProduct({ body: validated, id: id ?? '' });
  //     }
  //     setFormError(false);
  //   } catch (err) {
  //     setFormError(true);
  //   }
  // };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      const cleaned = {
        ...productForm,
        productId: productForm.stableSlug,
        // Avoid ObjectId CastError when the image select is blank
        productImageId: productForm.productImageId || null,
        fullPrice:
          productForm.fullPrice === '' || productForm.fullPrice === undefined
            ? undefined
            : Number(productForm.fullPrice),
        supportedHostApplications: productForm.supportedHostApplications.map(
          (h) => ({
            ...h,
            priceOverride:
              h.priceOverride === '' || h.priceOverride === undefined
                ? undefined
                : Number(h.priceOverride),
            downloadPage:
              h.downloadPage === '' || h.downloadPage === undefined
                ? undefined
                : h.downloadPage,
          }),
        ),
      };

      if (!productForm.stableSlug || productForm.stableSlug.trim() === '') {
        setFormError(true);
        return;
      }
      console.log('cleaned', cleaned);
      const validated = ProductFormSchema.parse(cleaned);
      console.log('validated', validated);

      if (
        validated.fullPrice === '' ||
        Number.isNaN(validated.fullPrice as any)
      ) {
        delete validated.fullPrice;
      }

      // // 🆕 Ensure slug fallback if left empty
      // if (!validated.stableSlug && validated.name) {
      //   validated.stableSlug = slugify(validated.name, {
      //     lower: true,
      //     strict: true,
      //   });
      // }

      if (productForm.sale === undefined || productForm.sale === null) {
        validated.sale = null;
      }

      // // explicitly send null when user cleared or reverted image
      // if (productForm.image === null) validated.image = null;

      const payload: any = validated;
      console.log('productForm', productForm);
      // console.log(
      //   'productForm.image instanceof File',
      //   productForm.image instanceof File,
      // );
      // If there's an image file, convert everything to FormData
      // if (productForm.image instanceof File) {
      //   const formData = new FormData();

      //   // append all regular fields
      //   Object.entries(validated).forEach(([key, value]) => {
      //     if (value === undefined || value === null) return;
      //     if (typeof value === 'object') {
      //       formData.append(key, JSON.stringify(value));
      //     } else {
      //       formData.append(key, String(value));
      //     }
      //   });

      //   // ✅ append image separately
      //   formData.append('image', productForm.image);

      //   // debug log
      //   for (const [key, value] of formData.entries()) {
      //     console.log('➡️', key, value);
      //   }

      //   payload = formData;
      // }

      if (mode === 'create') {
        createProduct(payload);
      } else {
        // Prefer Mongo _id from loaded details — route param may be stableSlug
        const mongoId = productDetails?._id;
        if (!mongoId) {
          console.error('Missing product _id for edit');
          setFormError(true);
          return;
        }
        editProduct({ body: payload, id: mongoId });
      }
      setFormError(false);
    } catch (err) {
      console.log(err);
      setFormError(true);
    }
  };

  const clearSale = () => {
    setProductForm({
      ...productForm,
      sale: undefined,
    });
  };

  const editSale = (newSale: ProductForm['sale']) => {
    setProductForm({
      ...productForm,
      sale: {
        salePrice: newSale?.salePrice ?? '',
        until: newSale?.until ?? null,
      },
    });
  };

  const highestHostVersion = React.useMemo(() => {
    if (!supportedHostApplications?.length) return null;

    const versions = supportedHostApplications
      .map((h) => Number(h.version))
      .filter((v) => !Number.isNaN(v));

    return versions.length ? Math.max(...versions) : null;
  }, [supportedHostApplications]);

  const upgradeVersionOptions = React.useMemo(() => {
    if (!highestHostVersion || highestHostVersion <= 1) return [];
    return Array.from({ length: highestHostVersion - 1 }, (_, i) => i + 1);
  }, [highestHostVersion]);

  return (
    <Container maxWidth="lg">
      <Box display="flex">
        <Box flex={1} p={3}>
          <Paper style={{ padding: '20px', backgroundColor: '#fff' }}>
            <form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Link to={mode === 'create' ? '/' : `/products/${id ?? ''}`}>
                    <Button startIcon={<ArrowBackIcon />}>
                      {mode === 'create'
                        ? 'Back to all products'
                        : 'Back to product details'}
                    </Button>
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h4">
                    {mode === 'edit' ? 'Edit Product' : 'Add a New Product'}
                  </Typography>
                </Grid>
                {/* <Grid item xs={4}>
                  <TextField
                    id="productId"
                    label="Image"
                    name="productId"
                    select
                    fullWidth
                    disabled={isLoading || imagesAreLoading}
                    value={productId}
                    onChange={handleChange}
                  >
                    {images.map(({ _id, name, key }) => (
                      <MenuItem key={_id} value={key}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <CardMedia
                              component="img"
                              image={`${baseUrl}/product-images/${key}`}
                              alt={name}
                              sx={{
                                width: 160,
                                height: 140,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                              }}
                            />
                            {name}
                          </Box>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              askDelete(_id, name);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    component="label"
                  >
                    Add Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        await handleUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </Button>

                  <ConfirmDialog
                    open={confirmOpen}
                    title="Delete image?"
                    message={
                      pendingDelete
                        ? `This will permanently remove "${pendingDelete.name}" from the image library. Products using this key will show a broken image until you assign another.`
                        : ''
                    }
                    confirmText="Delete"
                    cancelText="Cancel"
                    onClose={() => {
                      setConfirmOpen(false);
                      setPendingDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                  />
                </Grid> */}

                {/* <Grid item xs={4}>
                  <TextField
                    id="productId"
                    label="Product Image"
                    name="productId"
                    select
                    fullWidth
                    disabled={isLoading || imagesAreLoading}
                    value={productId}
                    onChange={handleChange}
                    SelectProps={{
                      renderValue: (selectedKey) => {
                        const selectedImage = images.find(
                          (img) => img.key === selectedKey,
                        );
                        if (!selectedImage) return 'Select image';
                        return (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              p: 2,
                              width: '100%',
                              height: 260,
                              backgroundColor: '#fafafa',
                              border: '1px solid #ddd',
                              borderRadius: 2,
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={`${baseUrl}/product-images/${selectedImage.key}`}
                              alt={selectedImage.name}
                              sx={{
                                maxHeight: 240,
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                        );
                      },
                    }}
                  >
                    {images.map(({ _id, name, key }) => (
                      <MenuItem key={_id} value={key}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <CardMedia
                              component="img"
                              image={`${baseUrl}/product-images/${key}`}
                              alt={name}
                              sx={{
                                width: 120,
                                height: 100,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                              }}
                            />
                            <Typography variant="body2">{name}</Typography>
                          </Box>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              askDelete(_id, name);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    component="label"
                    sx={{ mt: 2 }}
                  >
                    Add Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setPendingUpload(file);
                        setUploadDialogOpen(true);
                        e.target.value = '';
                      }}
                    />
                  </Button>

                  <ConfirmDialog
                    open={confirmOpen}
                    title="Delete image?"
                    message={
                      pendingDelete
                        ? `This will permanently remove "${pendingDelete.name}" from the image library. Products using this key will show a broken image until you assign another.`
                        : ''
                    }
                    confirmText="Delete"
                    cancelText="Cancel"
                    onClose={() => {
                      setConfirmOpen(false);
                      setPendingDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                  />

                  <ConfirmDialog
                    open={uploadDialogOpen}
                    title="Add Product Image"
                    message={
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2">
                          Enter the file name and key for this image:
                        </Typography>
                        <TextField
                          label="File Name"
                          fullWidth
                          value={uploadName}
                          onChange={(e) => setUploadName(e.target.value)}
                        />
                        <TextField
                          label="Key"
                          fullWidth
                          value={uploadKey}
                          onChange={(e) => setUploadKey(e.target.value)}
                          helperText="Usually lowercase with dashes (e.g. beauty-box-6)"
                        />
                      </Box>
                    }
                    confirmText="Upload"
                    cancelText="Cancel"
                    onClose={() => {
                      setUploadDialogOpen(false);
                      setPendingUpload(null);
                    }}
                    onConfirm={async () => {
                      if (!pendingUpload || !uploadName || !uploadKey) return;
                      await uploadImage({
                        file: pendingUpload,
                        name: uploadName,
                        key: uploadKey,
                      });
                      setUploadDialogOpen(false);
                      setPendingUpload(null);
                      setUploadName('');
                      setUploadKey('');
                    }}
                  />
                </Grid> */}

                <Grid item xs={4}>
                  <TextField
                    id="productImageId"
                    label="Product Image"
                    name="productImageId"
                    select
                    fullWidth
                    disabled={isLoading || imagesAreLoading}
                    value={productForm.productImageId || ''}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        productImageId: e.target.value,
                      })
                    }
                    SelectProps={{
                      renderValue: (selectedId) => {
                        const selectedImage = images.find(
                          (img) => img._id === selectedId,
                        );
                        if (!selectedImage) return 'Select image';
                        return (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              p: 2,
                              width: '100%',
                              height: 260,
                              backgroundColor: '#fafafa',
                              border: '1px solid #ddd',
                              borderRadius: 2,
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={`${baseUrl}/product-images/${selectedImage._id}`}
                              alt={selectedImage.filename}
                              sx={{
                                maxHeight: 240,
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                        );
                      },
                    }}
                  >
                    {images.map(({ _id, filename }) => (
                      <MenuItem key={_id} value={_id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            {/* <CardMedia
                              component="img"
                              image={`${baseUrl}/product-images/${_id}`}
                              alt={filename}
                              sx={{
                                // width: 120,
                                height: 100,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                              }}
                            /> */}

                            <LazyImg
                              src={`${baseUrl}/product-images/${_id}`}
                              alt={filename}
                              sx={{
                                height: 100,
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                              }}
                            />

                            {/* <Typography variant="body2">
                              {filename}
                            </Typography> */}
                          </Box>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              askDelete(_id, filename);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* upload new image */}
                  <Button
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    component="label"
                    sx={{ mt: 2 }}
                  >
                    Add Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          // Upload image and get result
                          const result = await uploadImage({ file }).unwrap();

                          // result likely returns the newly created image document
                          if (result?._id) {
                            setProductForm((prev) => ({
                              ...prev,
                              productImageId: result._id, // ✅ auto-select the new image
                            }));
                          }
                        } catch (err) {
                          console.error('Upload failed:', err);
                        } finally {
                          e.target.value = ''; // reset input so same file can be re-uploaded later
                        }
                      }}
                    />
                  </Button>

                  {/* delete confirmation dialog */}
                  <ConfirmDialog
                    open={confirmOpen}
                    title="Delete image?"
                    message={
                      pendingDelete
                        ? `This will permanently remove "${pendingDelete.name}" from the image library. Products using it will show a broken image until you assign another.`
                        : ''
                    }
                    confirmText="Delete"
                    cancelText="Cancel"
                    onClose={() => {
                      setConfirmOpen(false);
                      setPendingDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                  />
                </Grid>

                <Grid item xs={7} container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      id="name"
                      label="Product Name"
                      name="name"
                      required
                      fullWidth
                      disabled={isLoading}
                      value={name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="category"
                      label="Category"
                      name="category"
                      required
                      fullWidth
                      select
                      disabled={
                        isLoading ||
                        (mode === 'edit' &&
                          productForm.category === 'Subscription')
                      }
                      value={category}
                      onChange={handleChange}
                      onKeyUp={dropdownKeyEventHandler(
                        productCategoryOptions,
                        (option) =>
                          setProductForm({ ...productForm, category: option }),
                      )}
                    >
                      {productCategoryOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    {category === 'Upgrade' && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Typography variant="subtitle1" gutterBottom>
                            Upgrade Version Rules
                          </Typography>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!productForm.useCustomUpgradeVersions}
                                onChange={(e) => {
                                  const enabled = e.target.checked;
                                  setProductForm((prev) => ({
                                    ...prev,
                                    useCustomUpgradeVersions: enabled,
                                    validUpgradeFromVersions: enabled
                                      ? prev.validUpgradeFromVersions ?? []
                                      : [], // 🔑 clear when disabled
                                  }));
                                }}
                              />
                            }
                            label="Use custom valid upgrade versions"
                          />

                          {productForm.useCustomUpgradeVersions && (
                            <>
                              {!highestHostVersion && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                  Add supported host applications with versions
                                  first.
                                </Alert>
                              )}

                              {highestHostVersion && (
                                <Box mt={2}>
                                  <Typography variant="body2" gutterBottom>
                                    Accept upgrades from versions:
                                  </Typography>

                                  <Grid container spacing={1}>
                                    {upgradeVersionOptions.map((version) => (
                                      <Grid item key={version}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={productForm.validUpgradeFromVersions?.includes(
                                                version,
                                              )}
                                              onChange={(e) => {
                                                setProductForm((prev) => {
                                                  const current =
                                                    prev.validUpgradeFromVersions ??
                                                    [];
                                                  return {
                                                    ...prev,
                                                    validUpgradeFromVersions: e
                                                      .target.checked
                                                      ? [
                                                          ...current,
                                                          version,
                                                        ].sort((a, b) => a - b)
                                                      : current.filter(
                                                          (v) => v !== version,
                                                        ),
                                                  };
                                                });
                                              }}
                                            />
                                          }
                                          label={`v${version}`}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 1, display: 'block' }}
                                  >
                                    Latest version detected: v
                                    {highestHostVersion}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* 🆕 Stable Slug Family Selector */}
                  <Grid item xs={12}>
                    <StableSlugSelector
                      category={category}
                      value={productForm.stableSlug || ''}
                      onChange={(slug) =>
                        setProductForm({
                          ...productForm,
                          stableSlug: slug,
                          productId,
                        })
                      }
                    />
                    {/* 🆕 "Latest Version" toggle */}
                    <FormControlLabel
                      label="Mark as Latest Version in Product Family"
                      control={
                        <Checkbox
                          checked={!!productForm.isLatest}
                          onChange={(e) =>
                            setProductForm({
                              ...productForm,
                              isLatest: e.target.checked,
                            })
                          }
                        />
                      }
                    />
                    {/* {productForm.stableSlug &&
                      !allExistingSlugs.includes(productForm.stableSlug) && (
                        <Typography variant="caption" color="primary">
                          Creating new family: {productForm.stableSlug}
                        </Typography>
                      )} */}
                  </Grid>
                  {productForm.category === 'Subscription' ? (
                    <>
                      <Grid item xs={12}>
                        {mode === 'create' ? (
                          <TextField
                            id="priceId"
                            label="Stripe Price ID"
                            name="priceId"
                            required
                            fullWidth
                            disabled={isLoading}
                            value={priceId}
                            onChange={handleChange}
                          />
                        ) : (
                          <Typography>Stripe Price ID: {priceId}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {' '}
                        <TextField
                          id="downgradeId"
                          label="Stripe Downgrade Price ID"
                          name="downgradeId"
                          required
                          fullWidth
                          disabled={isLoading}
                          value={downgradeId}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          id="upgradeId"
                          label="Stripe Upgrade Price ID"
                          name="upgradeId"
                          required
                          fullWidth
                          disabled={isLoading}
                          value={upgradeId}
                          onChange={handleChange}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <TextField
                        id="fullPrice"
                        label="Full Price"
                        name="fullPrice"
                        type="number"
                        required
                        fullWidth
                        disabled={isLoading}
                        value={fullPrice ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (Number.isNaN(e.target.value)) {
                            setProductForm({ ...productForm, fullPrice: '' });
                            return;
                          }
                          setProductForm({
                            ...productForm,
                            fullPrice: e.target.valueAsNumber,
                          });
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    <FormControlLabel
                      label="Hidden"
                      disabled={isLoading}
                      control={
                        <Checkbox
                          checked={!!hidden}
                          onChange={(e) => {
                            setProductForm({
                              ...productForm,
                              hidden: e.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      label="Prioritize Product to the Top"
                      disabled={isLoading}
                      control={
                        <Checkbox
                          checked={!!priority}
                          onChange={(e) => {
                            setProductForm({
                              ...productForm,
                              priority: e.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      label="Show New Badge"
                      disabled={isLoading}
                      control={
                        <Checkbox
                          checked={!!showBadge}
                          onChange={(e) => {
                            setProductForm({
                              ...productForm,
                              showBadge: e.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      label="Show In Footer"
                      disabled={isLoading}
                      control={
                        <Checkbox
                          checked={!!showInFooter}
                          onChange={(e) => {
                            setProductForm({
                              ...productForm,
                              showInFooter: e.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </Grid>
                  {category !== 'Subscription' && (
                    <>
                      <Grid item>
                        <FormControlLabel
                          label="On Sale"
                          disabled={isLoading}
                          control={
                            <Checkbox
                              checked={!!sale}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  clearSale();
                                  return;
                                }
                                editSale({ salePrice: '', until: null });
                              }}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          id="salePrice"
                          label="Sale Price ($)"
                          name="salePrice"
                          type="number"
                          disabled={isLoading || !sale}
                          value={salePrice ?? ''}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (Number.isNaN(e.target.valueAsNumber)) {
                              editSale({ salePrice: '', until: until ?? null });
                              return;
                            }
                            editSale({
                              salePrice: e.target.valueAsNumber,
                              until: until ?? null,
                            });
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <DatePicker
                          label="Sale Ends On"
                          name="until"
                          disabled={isLoading || !sale}
                          value={dayjs(until)}
                          onChange={(end) => {
                            editSale({
                              salePrice: salePrice ?? '',
                              until: end ? end.toISOString() : '',
                            });
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ pt: 0 }}>
                  <TextField
                    id="keywords"
                    label="Product Hover Text"
                    name="keywords"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={keywords}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MarkdownEditor
                    name="Brief Description"
                    id="brief-description"
                    value={briefDescription}
                    handleChange={(value) =>
                      setProductForm({
                        ...productForm,
                        briefDescription: value ?? '',
                      })
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                  >
                    <Typography variant="caption">
                      Min Length: 10, Max Length: 1000
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color:
                          briefDescription.length < 10
                            ? 'red'
                            : briefDescription.length <= 1000
                            ? 'green'
                            : 'red',
                      }}
                    >
                      {briefDescription.length}/1000
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="supportedPlatforms"
                    label="Supported Platforms"
                    name="supportedPlatforms"
                    required
                    fullWidth
                    select
                    SelectProps={{ multiple: true }}
                    disabled={isLoading}
                    value={supportedPlatforms}
                    onChange={handleChange}
                  >
                    {platformOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <AssignSupportedHostApps
                  category={category}
                  supportedHostApps={supportedHostApplications}
                  changeSupportedHostApps={(apps: SupportedHostApps) => {
                    setProductForm({
                      ...productForm,
                      supportedHostApplications: [...apps],
                    });
                  }}
                />
                <Grid item xs={6}>
                  <TextField
                    id="productPage"
                    label="Product Page"
                    name="productPage"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={productPage}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="downloadPage"
                    label="Download Page"
                    name="downloadPage"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={downloadPage}
                    onChange={handleChange}
                    helperText="Default download URL. Host apps can override this below."
                  />
                </Grid>
                <Grid item xs={12}>
                  <MarkdownEditor
                    name="Full Description"
                    id="full-description"
                    value={fullDescription}
                    handleChange={(value) =>
                      setProductForm({
                        ...productForm,
                        fullDescription: value ?? '',
                      })
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                  >
                    <Typography variant="caption">
                      Min Length: 100, Max Length: 10000
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color:
                          fullDescription.length < 100
                            ? 'red'
                            : fullDescription.length <= 10000
                            ? 'green'
                            : 'red',
                      }}
                    >
                      {fullDescription.length}/10000
                    </Typography>
                  </Box>
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    {error?.data?.code === 'LATEST_CONFLICT' ? (
                      <Alert severity="error">
                        {error.data.message}{' '}
                        {error.data.existingLatest && (
                          <>
                            →{' '}
                            <Link
                              to={`/products/${error.data.existingLatest._id}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'underline',
                              }}
                            >
                              {error.data.existingLatest.name}
                            </Link>
                          </>
                        )}
                      </Alert>
                    ) : (
                      <Alert severity="error">{getErrorMessage(error)}</Alert>
                    )}
                  </Grid>
                )}
                {formError && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      There is an error in this form. Please correct it and try
                      again.
                    </Alert>
                  </Grid>
                )}
                {isSuccess && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      {mode === 'create'
                        ? 'Product created!'
                        : 'Product edited!'}
                    </Alert>
                  </Grid>
                )}
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                    // sx={{ mt: 2 }}
                    onClick={handleSubmit}
                  >
                    {isLoading ? <CircularProgress /> : submitButtonText}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default ProductFormPage;
